import {WorkflowStatusMinRes} from '../crm/status.contract';
import { Type } from 'class-transformer';

export class ProjectBudgetStatsRes {
    budgetId: number = 0;
    name: string = '';
    reference: string = '';
    orderNumber: string = '';
    userId?: number | null = null;
    userName: string = '';
    sourceOfferId?: number | null = null;
    sourceOfferNumber: string = '';

    @Type(() => Date)
    startDate: Date = new Date();

    @Type(() => Date)
    endDate?: Date | null = null;

    totalHours: number = 0;
    actualWorkedHours: number = 0;
    invoicedHours: number = 0;
    remainingHours: number = 0;
    unbilledWorkedHours: number = 0;
    overrunHours: number = 0;
    preStartAssignedHours: number = 0;
    hourlyRate: number = 0;
    totalNetto: number = 0;
    actualNetto: number = 0;
    remainingNetto: number = 0;
    utilizationPercent: number = 0;
}


export class ProjectBudgetSnapshotRes {
    @Type(() => Date)
    asOfUtc: Date = new Date();
    actualWorkedHours: number = 0;
    budgetTotalHours: number = 0;
    budgetActualWorkedHours: number = 0;
    budgetInvoicedHours: number = 0;
    budgetRemainingHours: number = 0;
    budgetUnbilledWorkedHours: number = 0;
    budgetOverrunHours: number = 0;
    unassignedWorkedHours: number = 0;
    budgetTotalNetto: number = 0;
    budgetActualNetto: number = 0;
    budgetRemainingNetto: number = 0;

    @Type(() => ProjectUserStatsRes)
    users: ProjectUserStatsRes[] = [];
}

export class ProjectTaskStatsRes {
    taskId: string = '';
    name: string = '';

    @Type(() => Date)
    currentStart: Date = new Date();

    @Type(() => Date)
    currentEnd: Date = new Date();

    plannedTaskHours: number = 0;
    plannedAppointmentHours: number = 0;
    actualWorkedHours: number = 0;

    constructor(data?: Partial<ProjectTaskStatsRes>) {
        Object.assign(this, data);
    }
}

export class CalendarExportFormConfig  {
    start!: Date;
    end!: Date;
    selectedProject?: number;
    availableProjects?: ProjectStatsRes[] | null;
    availableUsers?: ProjectUserStatsRes[] | null;
    separateFiles?: boolean;

    constructor(data: Partial<CalendarExportFormConfig>) {
        Object.assign(this, data);
    }
}

export class ProjectUserStatsRes {
    userId: number = 0;
    userName: string = '';
    plannedAppointmentHours: number = 0;
    actualWorkedHours: number = 0;
    assignedBudgetHours: number = 0;
    coveredByBudgetHours: number = 0;
    invoicedBudgetHours: number = 0;
    remainingAssignedBudgetHours: number = 0;
    uncoveredActualHours: number = 0;

    constructor(data?: Partial<ProjectUserStatsRes>) {
        Object.assign(this, data);
    }
}

export class ProjectYearStatsRes {
    year: number = 0;

    appointmentCount: number = 0;
    workTimeEntryCount: number = 0;

    plannedAppointmentHours: number = 0;
    actualWorkedHours: number = 0;
    plannedVsActualDeltaHours: number = 0;
    coveredByBudgetHours: number = 0;
    uncoveredActualHours: number = 0;

    @Type(() => ProjectUserStatsRes)
    users: ProjectUserStatsRes[] = [];

    constructor(data?: Partial<ProjectYearStatsRes>) {
        Object.assign(this, data);
    }
}

export class ProjectStatsRes {
    projectId: number = 0;
    name: string = '';
    isActive: boolean = true;
    currentWorkflowStatus?: WorkflowStatusMinRes | null = null;
    customerId: number = 0;
    customerName: string = '';
    customerHasAddress: boolean = false;
    timesheetCustomerId?: number | null = null;
    timesheetCustomerName: string = '';
    timesheetCustomerHasAddress: boolean = false;

    @Type(() => Date)
    requestedFromUtc?: Date | null = null;

    @Type(() => Date)
    requestedToUtc?: Date | null = null;

    @Type(() => Date)
    coverageFromUtc?: Date | null = null;

    @Type(() => Date)
    coverageToUtc?: Date | null = null;

    taskCount: number = 0;
    teamMemberCount: number = 0;

    appointmentCount: number = 0;
    workTimeEntryCount: number = 0;
    recentWorklogCount: number = 0;

    plannedTaskHours: number = 0;
    plannedAppointmentHours: number = 0;
    actualWorkedHours: number = 0;
    plannedVsActualDeltaHours: number = 0;

    projectVolumeBrutto?: number | null = null;
    projectVolumeBruttoOpen: number = 0;

    budgetTotalHours: number = 0;
    budgetActualWorkedHours: number = 0;
    budgetInvoicedHours: number = 0;
    budgetRemainingHours: number = 0;
    budgetUnbilledWorkedHours: number = 0;
    budgetOverrunHours: number = 0;
    unassignedWorkedHours: number = 0;
    budgetTotalNetto: number = 0;
    budgetActualNetto: number = 0;
    budgetRemainingNetto: number = 0;
    budgetUtilizationPercent: number = 0;
    budgetHourlyRates: string = '';

    @Type(() => ProjectBudgetSnapshotRes)
    previousMonthSnapshot?: ProjectBudgetSnapshotRes | null = null;

    @Type(() => ProjectBudgetStatsRes)
    budgets: ProjectBudgetStatsRes[] = [];

    @Type(() => ProjectTaskStatsRes)
    tasks: ProjectTaskStatsRes[] = [];

    @Type(() => ProjectUserStatsRes)
    users: ProjectUserStatsRes[] = [];

    @Type(() => ProjectYearStatsRes)
    years: ProjectYearStatsRes[] = [];

    constructor(data?: Partial<ProjectStatsRes>) {
        Object.assign(this, data);
    }
}

export class CompanyProjectYearStatsRes {
    year: number = 0;
    projectCount: number = 0;

    totalPlannedAppointmentHours: number = 0;
    totalActualWorkedHours: number = 0;
    totalPlannedVsActualDeltaHours: number = 0;
    totalCoveredByBudgetHours: number = 0;
    totalUncoveredActualHours: number = 0;

    @Type(() => ProjectUserStatsRes)
    users: ProjectUserStatsRes[] = [];

    constructor(data?: Partial<CompanyProjectYearStatsRes>) {
        Object.assign(this, data);
    }
}

export class CompanyProjectStatsRes {
    companyId: number = 0;
    companyName: string = '';

    executingCompanyId!: number;
    executingCompanyName: string = '';

    @Type(() => Date)
    requestedFromUtc?: Date | null = null;

    @Type(() => Date)
    requestedToUtc?: Date | null = null;

    @Type(() => Date)
    coverageFromUtc?: Date | null = null;

    @Type(() => Date)
    coverageToUtc?: Date | null = null;

    projectCount: number = 0;

    totalPlannedTaskHours: number = 0;
    totalPlannedAppointmentHours: number = 0;
    totalActualWorkedHours: number = 0;
    totalPlannedVsActualDeltaHours: number = 0;

    totalProjectVolumeBrutto: number = 0;
    totalProjectVolumeBruttoOpen: number = 0;

    totalBudgetHours: number = 0;
    totalBudgetActualWorkedHours: number = 0;
    totalBudgetInvoicedHours: number = 0;
    totalBudgetRemainingHours: number = 0;
    totalBudgetUnbilledWorkedHours: number = 0;
    totalBudgetOverrunHours: number = 0;
    totalUnassignedWorkedHours: number = 0;
    totalBudgetNetto: number = 0;
    totalBudgetActualNetto: number = 0;
    totalBudgetRemainingNetto: number = 0;

    @Type(() => ProjectBudgetSnapshotRes)
    previousMonthSnapshot?: ProjectBudgetSnapshotRes | null = null;

    @Type(() => ProjectUserStatsRes)
    users: ProjectUserStatsRes[] = [];

    @Type(() => CompanyProjectYearStatsRes)
    years: CompanyProjectYearStatsRes[] = [];

    @Type(() => ProjectStatsRes)
    projects: ProjectStatsRes[] = [];

    constructor(data?: Partial<CompanyProjectStatsRes>) {
        Object.assign(this, data);
    }
}

export class ProjectUserYearHoursRes {
    year: number = 0;

    userId: number = 0;
    userName: string = '';

    projectId: number = 0;
    projectName: string = '';

    customerId: number = 0;
    customerName: string = '';

    workTimeEntryCount: number = 0;
    actualWorkedHours: number = 0;
    assignedBudgetHours: number = 0;
    coveredByBudgetHours: number = 0;
    uncoveredHours: number = 0;
    remainingAssignedBudgetHours: number = 0;

    @Type(() => Date)
    firstEntryUtc: Date = new Date();

    @Type(() => Date)
    lastEntryUtc: Date = new Date();

    constructor(data?: Partial<ProjectUserYearHoursRes>) {
        Object.assign(this, data);
    }
}

export class CompanyProjectUserYearHoursRes {
    companyId: number = 0;
    companyName: string = '';

    @Type(() => Date)
    requestedFromUtc?: Date | null = null;

    @Type(() => Date)
    requestedToUtc?: Date | null = null;

    @Type(() => Date)
    coverageFromUtc?: Date | null = null;

    @Type(() => Date)
    coverageToUtc?: Date | null = null;

    projectCount: number = 0;
    userCount: number = 0;
    workTimeEntryCount: number = 0;
    totalActualWorkedHours: number = 0;
    totalCoveredByBudgetHours: number = 0;
    totalUncoveredHours: number = 0;

    @Type(() => ProjectUserYearHoursRes)
    rows: ProjectUserYearHoursRes[] = [];

    constructor(data?: Partial<CompanyProjectUserYearHoursRes>) {
        Object.assign(this, data);
    }
}


export class ProjectBudgetUsageUserRes {
    userId: number = 0;
    userName: string = '';
    projectCount: number = 0;
    budgetCount: number = 0;
    assignedBudgetHours: number = 0;
    actualWorkedHours: number = 0;
    coveredByBudgetHours: number = 0;
    sharedBudgetCoveredHours: number = 0;
    invoicedHours: number = 0;
    remainingAssignedBudgetHours: number = 0;
    uncoveredHours: number = 0;
    utilizationPercent: number = 0;

    constructor(data?: Partial<ProjectBudgetUsageUserRes>) {
        Object.assign(this, data);
    }
}

export class ProjectBudgetUsageProjectUserRes extends ProjectBudgetUsageUserRes {
    projectId: number = 0;
    projectName: string = '';
    customerId: number = 0;
    customerName: string = '';

    constructor(data?: Partial<ProjectBudgetUsageProjectUserRes>) {
        super(data);
        Object.assign(this, data);
    }
}

export class ProjectBudgetUsagePeriodRes {
    @Type(() => Date)
    periodStartUtc: Date = new Date();

    @Type(() => Date)
    periodEndUtc: Date = new Date();

    year: number = 0;
    month: number = 0;
    userId: number = 0;
    userName: string = '';
    projectId: number = 0;
    projectName: string = '';
    customerId: number = 0;
    customerName: string = '';
    workTimeEntryCount: number = 0;
    actualWorkedHours: number = 0;
    coveredByBudgetHours: number = 0;
    sharedBudgetCoveredHours: number = 0;
    invoicedHours: number = 0;
    uncoveredHours: number = 0;

    constructor(data?: Partial<ProjectBudgetUsagePeriodRes>) {
        Object.assign(this, data);
    }
}

export class CompanyProjectBudgetUsageRes {
    companyId: number = 0;
    companyName: string = '';

    @Type(() => Date)
    requestedFromUtc?: Date | null = null;

    @Type(() => Date)
    requestedToUtc?: Date | null = null;

    @Type(() => Date)
    coverageFromUtc?: Date | null = null;

    @Type(() => Date)
    coverageToUtc?: Date | null = null;

    projectCount: number = 0;
    userCount: number = 0;
    workTimeEntryCount: number = 0;
    totalBudgetHours: number = 0;
    totalActualWorkedHours: number = 0;
    totalCoveredByBudgetHours: number = 0;
    totalInvoicedHours: number = 0;
    totalRemainingBudgetHours: number = 0;
    totalUncoveredHours: number = 0;

    @Type(() => ProjectBudgetUsageUserRes)
    users: ProjectBudgetUsageUserRes[] = [];

    @Type(() => ProjectBudgetUsageProjectUserRes)
    projectUsers: ProjectBudgetUsageProjectUserRes[] = [];

    @Type(() => ProjectBudgetUsagePeriodRes)
    periods: ProjectBudgetUsagePeriodRes[] = [];

    constructor(data?: Partial<CompanyProjectBudgetUsageRes>) {
        Object.assign(this, data);
    }
}

export class ProjectStatsQueryReq {
    @Type(() => Date)
    fromUtc?: Date | null = null;

    @Type(() => Date)
    toUtc?: Date | null = null;

    projectIds?: number[] | null = null;
    customerIds?: number[] | null = null;
    years?: number[] | null = null;

    constructor(data?: Partial<ProjectStatsQueryReq>) {
        Object.assign(this, data);
    }
}
